import React from 'react';

import { usePathname } from 'expo-router';
import {
  MenuNavigationTabLink,
  MenuNavigationTabText,
} from 'features/menu/components/menu-navigation-tabs/styled';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { ServiceModeBar } from 'components/app-header/mobile-header/service-mode-bar';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import {
  resolveNativeTabsHeaderTopInset,
  useRootSafeAreaInsets,
} from 'components/root-safe-area-insets-provider';
import { Skeleton } from 'components/ui/skeleton';
import { useEnableNativeTabBarExperiment } from 'experiments/use-enable-native-tab-bar-experiment';
import {
  useExposeRemoveMenuFavoritesExperiment,
  useRemoveMenuFavoritesExperiment,
} from 'experiments/use-remove-menu-favorites-experiment';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { t } from 'state/intl/translations';
import { useMainMenuContext } from 'state/menu/main-menu';
import { spacing } from 'styles/constants';
import { routes } from 'utils/routing';

const MENU_TAB_SKELETONS = 3;

const MenuTabsLoading = () => (
  <View
    accessibilityRole="progressbar"
    accessibilityLabel="Loading menu navigation"
    style={styles.loadingTabs}
    testID="menu-tabs-loading"
  >
    {Array.from({ length: MENU_TAB_SKELETONS }, (_, index) => (
      <Skeleton key={index} shape="rectangle" style={styles.loadingTab} />
    ))}
  </View>
);

export const MenuTabs = () => {
  const pathname = usePathname();

  const isDesktop = useIsDesktopBreakpoint();
  const removeMenuFavorites = useRemoveMenuFavoritesExperiment();
  useExposeRemoveMenuFavoritesExperiment();

  const logTabSelected = (entry: string, tabItemId: string): void => {
    logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
      'Selected Tab': entry,
    });
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
        componentId: tabItemId,
        name: entry,
      },
    });
  };

  // if the app is transitioning to a different screen than the menu,
  // then we should force prevent displaying the menu tabs.
  // if (!isOnMenuRoute) {
  //   return <MenuHeaderTransition />;
  // }

  const mainMenuLabelKey = 'fullMenu';
  const recentsLabelKey = 'recents';
  const favoriteLabelKey = 'favorite';

  const mainMenuTab = {
    tabItemId: mainMenuLabelKey,
    label: t(mainMenuLabelKey),
    to: routes.menu,
  };

  const recentItemsTab = {
    tabItemId: recentsLabelKey,
    label: t(recentsLabelKey),
    to: routes.menuRecent,
  };

  const favoritesTab =
    removeMenuFavorites !== false
      ? null
      : {
          tabItemId: favoriteLabelKey,
          label: t(favoriteLabelKey),
          to: routes.favorites,
        };

  const menuTabs = [mainMenuTab, recentItemsTab, favoritesTab].filter(item => item !== null);

  const activeTab = menuTabs.findIndex(item => item?.to === pathname);

  const tabWidth = `${100 / menuTabs.length}%`;

  return (
    <>
      {isDesktop ? (
        // Desktop needs the App header to access the cart
        <StandardMobileHeader />
      ) : (
        <ServiceModeBar hideTopOrderStatusBar />
      )}
      <CenteredRowContainer testID="menu-tabs">
        {removeMenuFavorites === undefined ? (
          // This experiment must not guess a variant on cold start: rendered tabs would otherwise
          // be replaced once the assignment resolves. Remove this state when the experiment ends.
          <MenuTabsLoading />
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ width: '100%' }}
            accessibilityRole="tablist"
          >
            {menuTabs.map(({ label, to, tabItemId }, index) => (
              <MenuNavigationTabLink
                key={label}
                linkPath={to}
                testID={`tabbed-menu-${label}`}
                active={index === activeTab}
                onPress={() => logTabSelected(label, tabItemId)}
                minWidth={tabWidth}
                accessibilityRole="tab"
                aria-selected={index === activeTab}
                accessibilityLabel={index === activeTab ? `${label}, selected` : ''}
              >
                <MenuNavigationTabText active={index === activeTab}>{label}</MenuNavigationTabText>
              </MenuNavigationTabLink>
            ))}
          </ScrollView>
        )}
      </CenteredRowContainer>
    </>
  );
};

/** TODO
 * - switch these tabs to https://github.com/EvanBacon/expo-router-top-tabs
 * - wrap with CenteredRowContainer
 * */
export const MenuTabsWrap: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { top } = useSafeAreaInsets();
  const { top: rootTop } = useRootSafeAreaInsets();
  const { isTreatment: isNativeTabBarEnabled } = useEnableNativeTabBarExperiment();
  const topInset = resolveNativeTabsHeaderTopInset({
    localTop: top,
    rootTop,
    useNativeTabsRootInset: isNativeTabBarEnabled,
  });

  const { activeDayParts } = useMainMenuContext();
  const activeDayPartKeys = activeDayParts?.map(dayPart => dayPart.key).join(',') ?? '';

  return (
    <View
      style={{ flex: 1, paddingTop: topInset }}
      testID="menu-tabs-wrap"
      dataSet={{ activeDayPartKeys }}
    >
      <MenuTabs />
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  loadingTabs: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  loadingTab: {
    flex: 1,
    marginHorizontal: spacing.$2,
  },
});
